import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const SouthTexasCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="South Texas: 22% Achievement Gain With Immediate Feedback | Curipod Case Study"
      subTitle="See how Curipod drives measurable student growth — a 22% increase in Meets and Masters for 6th-grade RLA in South Texas."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>Immediate Feedback Loop Raises Achievement 22%</HeroTitle>
            <HeroSubtitle>How One Teacher Transformed 6th-Grade Writing</HeroSubtitle>
            <HeroDescription>
              How one teacher found a way to do the impossible — engage every student in
              writing and provide instant feedback on each writing piece — an approach
              that spread across 6th-grade classrooms, driving unprecedented achievement.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>South Texas</MetaBadge>
              <MetaBadge>6th Grade RLA</MetaBadge>
              <MetaBadge>2023–2024 School Year</MetaBadge>
            </HeroMeta>
            <HeroCtaWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                </a>
              </Link>
              <Link href="/quotes?step=role" passHref>
                <a>
                  <HeroCtaButton type="primary">Book a demo</HeroCtaButton>
                </a>
              </Link>
            </HeroCtaWrapper>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    46<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Meets &amp; Masters</StatLabel>
                  <StatYear>2023 · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>→</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    56<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Meets &amp; Masters</StatLabel>
                  <StatYear>2024 · With Curipod</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    22<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Achievement Increase</StatLabel>
                  <StatYear>6th Grade · With Curipod</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* DOWNLOAD CTA (below hero) */}
      <DownloadCtaBanner>
        <MaxWidthContainer>
          <DownloadCtaInner>
            <DownloadCtaLabel>
              Want the full story? Download the PDF version of this case study.
            </DownloadCtaLabel>
            <a
              href="/Curipod_Case_Study_South_Texas.pdf"
              download="Curipod_Case_Study_South_Texas.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 44 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadCtaInner>
        </MaxWidthContainer>
      </DownloadCtaBanner>

      {/* THE CHALLENGE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>THE CHALLENGE</SectionTag>
              <SectionTitle>Moving the Needle, Now</SectionTitle>
              <SectionText>
                Teaching in a school with 88% of students economically disadvantaged, Ms.
                G has dedicated years to supporting her students — creating engaging
                lessons, offering one-on-one help, and working with families. Despite her
                efforts, nearly three-quarters of her students struggled to meet
                grade-level expectations on the state&apos;s Reading Language Arts (RLA)
                test.
              </SectionText>
              <SectionText>
                Middle school teachers know all too well, it can take a lot to get
                students excited about writing. There had to be a way to move the needle,
                now.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  I could be doing cartwheels to get their attention, and even that
                  wouldn&apos;t work.
                </QuoteText>
                <QuoteAttrib>— Ms. G, 6th Grade Teacher</QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamCardTitle>Impact at a Glance</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>TOOL</TeamRole>
                  <TeamName>Curipod Test Prep Customizable Lessons</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>FREQUENCY</TeamRole>
                  <TeamName>Delivered 1–2x per week</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>RESULT</TeamRole>
                  <TeamName>22% increase in Meets &amp; Masters</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>CONTEXT</TeamRole>
                  <TeamName>88% economically disadvantaged students</TeamName>
                </TeamMember>
              </TeamMemberList>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* THREE THINGS THAT MADE THE DIFFERENCE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              THE CURIPOD DIFFERENCE
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Three Things That Made the Difference
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Then she tried Curipod. Lightbulbs switched on. &ldquo;Every single lesson
              blew their minds,&rdquo; she said. What made writing in Curipod so different
              from everything Ms. G had tried so far?
            </SectionText>

            <ImpactGrid>
              <ImpactCard>
                <ImpactNumber>1</ImpactNumber>
                <ImpactTitle>Student Agency</ImpactTitle>
                <ImpactQuote>
                  Curipod can generate standards-aligned writing activities on any topic
                  students care about. They could do a full writing lesson on Taylor Swift
                  or the World Cup.
                </ImpactQuote>
              </ImpactCard>

              <ImpactCard>
                <ImpactNumber>2</ImpactNumber>
                <ImpactTitle>Instant Feedback</ImpactTitle>
                <ImpactQuote>
                  Curipod gave immediate AI feedback on every response, motivating
                  students to engage more deeply. When they received feedback right after
                  putting in effort, students were more eager to review, understand their
                  mistakes, and improve while the writing was still fresh in their minds.
                </ImpactQuote>
              </ImpactCard>

              <ImpactCard $wide>
                <ImpactNumber>3</ImpactNumber>
                <ImpactTitle>Social Writing</ImpactTitle>
                <ImpactQuote>
                  Writing became a shared experience as students discussed their work and
                  feedback with each other, turning a typically solitary task into an
                  exciting, social one.
                </ImpactQuote>
              </ImpactCard>
            </ImpactGrid>

            <ScreenshotRow>
              <ScreenshotCard>
                <ScreenshotImageWrap>
                  <Image
                    src="/case-study-south-texas/staar-lesson-slide.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod Texas STAAR lesson slide"
                  />
                </ScreenshotImageWrap>
                <ScreenshotCaption>
                  Standards-aligned STAAR lesson in Curipod
                </ScreenshotCaption>
              </ScreenshotCard>
              <ScreenshotCard>
                <ScreenshotImageWrap>
                  <Image
                    src="/case-study-south-texas/writing-prompt.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod writing prompt about the World Cup"
                  />
                </ScreenshotImageWrap>
                <ScreenshotCaption>
                  Writing prompts on topics students care about
                </ScreenshotCaption>
              </ScreenshotCard>
            </ScreenshotRow>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* THE FEEDBACK LOOP */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            THE FEEDBACK LOOP
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            Immediate Feedback for Every Student
          </SectionTitle>

          <TwoColumnGrid>
            <div>
              <SectionText>
                Ms. G&apos;s fellow 6th grade teachers noticed too. In February, with the
                state test approaching in a few short months, she led her team in using
                Curipod to build students&apos; writing skills.
              </SectionText>
              <SectionText>
                The 6th grade teaching team of three shared their state&apos;s Test Prep
                Customizable Lessons from the Curipod library. Based on research that
                proves the power of feedback on writing achievement, the lessons made it
                possible to provide every student with an immediate feedback loop every
                time:
              </SectionText>
              <FeedbackStepList>
                <FeedbackStep>Students typed their response in Curipod.</FeedbackStep>
                <FeedbackStep>
                  In real time, they received a score and actionable feedback aligned to
                  their state&apos;s writing rubric, including tips for how to improve.
                </FeedbackStep>
                <FeedbackStep>
                  Then, students got a chance to revise their writing, all in the same
                  class period.
                </FeedbackStep>
              </FeedbackStepList>
            </div>

            <FeedbackScreenshots>
              <ScreenshotCard>
                <ScreenshotImageWrap $tall>
                  <Image
                    src="/case-study-south-texas/feedback-example.png"
                    layout="fill"
                    objectFit="contain"
                    alt="AI feedback example showing score and improvement tips"
                  />
                </ScreenshotImageWrap>
                <ScreenshotCaption>
                  Real-time score and actionable feedback
                </ScreenshotCaption>
              </ScreenshotCard>
              <ScreenshotCard>
                <ScreenshotImageWrap>
                  <Image
                    src="/case-study-south-texas/feedback-poll.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Student poll on how to improve based on feedback"
                  />
                </ScreenshotImageWrap>
                <ScreenshotCaption>Students reflect on how to improve</ScreenshotCaption>
              </ScreenshotCard>
            </FeedbackScreenshots>
          </TwoColumnGrid>

          <BlockQuote style={{ maxWidth: 720, margin: '40px auto 0' }}>
            <QuoteMark>&ldquo;</QuoteMark>
            <QuoteText>
              I saw students really eager and excited to learn! Especially wanting to
              write. When a 6th grader is willing to write&nbsp;.&nbsp;.&nbsp;. I got
              goosebumps.
            </QuoteText>
            <QuoteAttrib>— Ms. G&apos;s Principal</QuoteAttrib>
          </BlockQuote>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              RESULTS THAT SPEAK FOR THEMSELVES
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              Greatest Percentage of Meets &amp; Masters
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              That June, the state&apos;s test results were released. The entire school
              was blown away. For the first time, the 6th graders earned the greatest
              percentage of students earning Meets or Masters — an improvement of 22% from
              the previous year.
            </SectionText>

            <ResultsTable>
              <ResultsTableHeader>
                <span>% Achieving Meets or Masters</span>
                <ResultsTableYears>
                  <YearBadge>2023</YearBadge>
                  <YearBadge $highlight>2024</YearBadge>
                </ResultsTableYears>
              </ResultsTableHeader>
              <ResultsTableRow $highlight>
                <ResultsTableLabel>
                  <ResultsTableTag>with Curipod Test Prep</ResultsTableTag>
                  6th Grade
                </ResultsTableLabel>
                <ResultsTableValues>
                  <ResultsTableValue>46%</ResultsTableValue>
                  <ResultsTableArrow>→</ResultsTableArrow>
                  <ResultsTableValue $highlight>56%</ResultsTableValue>
                </ResultsTableValues>
              </ResultsTableRow>
              <ResultsTableRow>
                <ResultsTableLabel>
                  <ResultsTableTag>No Curipod</ResultsTableTag>
                  7th Grade
                </ResultsTableLabel>
                <ResultsTableValues>
                  <ResultsTableValue>66%</ResultsTableValue>
                  <ResultsTableArrow>→</ResultsTableArrow>
                  <ResultsTableValue>57%</ResultsTableValue>
                </ResultsTableValues>
              </ResultsTableRow>
              <ResultsTableRow>
                <ResultsTableLabel>8th Grade</ResultsTableLabel>
                <ResultsTableValues>
                  <ResultsTableValue>53%</ResultsTableValue>
                  <ResultsTableArrow>→</ResultsTableArrow>
                  <ResultsTableValue>42%</ResultsTableValue>
                </ResultsTableValues>
              </ResultsTableRow>
            </ResultsTable>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                This captured the 7th and 8th grade teachers&apos;s attention. They all
                got thinking, &lsquo;They&apos;re closing the gap! How did they do
                that?&rsquo;
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Ms. G&apos;s Principal
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultNote>
              &ldquo;Look at the scores,&rdquo; Ms. G&apos;s principal went on to say.
              &ldquo;Because that&apos;s the bottom line.&rdquo;
            </ResultNote>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Sparking Curiosity, Even During Test Prep
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Built on research-backed best practices for student engagement and learning,
              Curipod uses interactive tools and artificial intelligence to help teachers
              provide instruction that authentically engages students in learning.
            </SectionText>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                With Curipod, there is always something to spark students&apos; curiosity
                — even during test prep.
              </FinalQuoteText>
              <QuoteAttrib>— Ms. G, 6th Grade Teacher</QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {['Feedback', 'Writing', 'Engagement', 'Test Prep'].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
              <Link href="/quotes?step=role" passHref>
                <a>
                  <Button
                    type="tertiary"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Book a demo
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the South Texas case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_South_Texas.pdf"
              download="Curipod_Case_Study_South_Texas.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default SouthTexasCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;

  @media ${MOBILE} {
    align-items: flex-start;
  }
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── DOWNLOAD CTA BANNER (below hero) ──────────────────────────────────────── */

const DownloadCtaBanner = styled.div`
  background: ${theme.colors.beigeDark};
  border-bottom: 1px solid rgba(249, 151, 70, 0.2);
  padding: 16px 0;
`;

const DownloadCtaInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
`;

const DownloadCtaLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.text.dark};

  @media ${MOBILE} {
    font-size: 14px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactNumber = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin-bottom: 8px;
`;

/* ─── SCREENSHOTS ────────────────────────────────────────────────────────────── */

const ScreenshotRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ScreenshotCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: ${theme.effects.defaultShadow};
`;

const ScreenshotImageWrap = styled.div<{ $tall?: boolean }>`
  position: relative;
  width: 100%;
  padding-bottom: ${({ $tall }) => ($tall ? '55%' : '56.25%')};
  border-radius: 8px;
  overflow: hidden;
`;

const ScreenshotCaption = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 12px 0 0;
`;

/* ─── FEEDBACK LOOP ──────────────────────────────────────────────────────────── */

const FeedbackStepList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0 0;
`;

const FeedbackStep = styled.li`
  font-size: 17px;
  color: ${theme.colors.text.dark};
  line-height: 1.7;
  padding: 12px 0 12px 28px;
  position: relative;
  border-left: 3px solid ${theme.colors.orange};
  margin-left: 12px;

  &::before {
    content: '';
    position: absolute;
    left: -7px;
    top: 18px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${theme.colors.orange};
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const FeedbackScreenshots = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultsTable = styled.div`
  width: 100%;
  max-width: 560px;
  margin-bottom: 48px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
`;

const ResultsTableHeader = styled.div`
  background: ${theme.colors.white};
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.black};

  @media ${MOBILE} {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const ResultsTableYears = styled.div`
  display: flex;
  gap: 8px;
`;

const YearBadge = styled.span<{ $highlight?: boolean }>`
  background: ${({ $highlight }) => ($highlight ? theme.colors.orange : '#fce4b8')};
  color: ${({ $highlight }) => ($highlight ? theme.colors.white : theme.colors.black)};
  font-size: 14px;
  font-weight: 700;
  padding: 4px 16px;
  border-radius: 6px;
`;

const ResultsTableRow = styled.div<{ $highlight?: boolean }>`
  background: ${({ $highlight }) =>
    $highlight ? theme.colors.orange : 'rgba(255, 255, 255, 0.95)'};
  color: ${({ $highlight }) => ($highlight ? theme.colors.white : theme.colors.black)};
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  @media ${MOBILE} {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const ResultsTableLabel = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const ResultsTableTag = styled.div`
  font-size: 11px;
  font-weight: 600;
  opacity: 0.85;
  margin-bottom: 2px;
`;

const ResultsTableValues = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ResultsTableValue = styled.span<{ $highlight?: boolean }>`
  font-size: 20px;
  font-weight: 700;
  ${({ $highlight }) =>
    $highlight &&
    css`
      font-size: 24px;
    `}
`;

const ResultsTableArrow = styled.span`
  font-size: 18px;
  opacity: 0.7;
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0;
  font-style: italic;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
